import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/es';

dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);

/**
 * Pure transforms for the Reporteria admin page.
 * All functions are empty-safe: missing/invalid input yields [] or 0.
 * The stats endpoint returns `*_month` dicts keyed by "DD/MM/YYYY" (friendly_date).
 * We parse those keys with dayjs(key, 'DD/MM/YYYY') for chronological sorting.
 */

const FRIENDLY_DATE_FORMAT = 'DD/MM/YYYY';
const SPANISH_FRIENDLY_DATE_FORMAT = 'D [de] MMMM[,] YYYY';

function parseFriendlyDate(key) {
  if (!key) return null;
  const k = String(key);
  // Try DD/MM/YYYY first (some modes/endpoints may use this)
  let d = dayjs(k, FRIENDLY_DATE_FORMAT);
  if (d.isValid()) return d;
  // Spanish friendly format: "20 de agosto, 2026" / "5 de septiembre, 2026"
  d = dayjs(k, SPANISH_FRIENDLY_DATE_FORMAT, 'es');
  if (d.isValid()) return d;
  return null;
}

function safeNumber(value) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Map a UI range to endpoint params { start, end, mode }.
 * Dates are DDMMYY (2-digit year) per the backend contract.
 * We use `daily` for ALL ranges and bucket the 6m case client-side
 * into weeks (see bucketWeekly) to avoid the unverified weekly endpoint shape.
 */
export function rangeToParams(range) {
  const today = dayjs();
  const fmt = (d) => d.format('DDMMYY');
  switch (range) {
    case '24h':
      return { start: fmt(today), end: fmt(today), mode: 'daily' };
    case 'month':
      return { start: fmt(today.subtract(29, 'day')), end: fmt(today), mode: 'daily' };
    case '6m':
      return { start: fmt(today.subtract(6, 'month')), end: fmt(today), mode: 'daily' };
    case 'week':
    default:
      return { start: fmt(today.subtract(6, 'day')), end: fmt(today), mode: 'daily' };
  }
}

/**
 * Visits time series from visits.visits_month.
 * Returns [{ label: 'DD/MM', value, date }] sorted chronologically.
 */
export function toVisitsSeries(visits) {
  if (!visits || typeof visits !== 'object') return [];
  const month = visits.visits_month;
  if (!month || typeof month !== 'object') return [];
  const out = [];
  for (const [key, entry] of Object.entries(month)) {
    const date = parseFriendlyDate(key);
    if (!date) continue;
    const value = safeNumber(entry && typeof entry === 'object' ? entry.amount : entry);
    out.push({ label: date.format('DD/MM'), value, date });
  }
  out.sort((a, b) => a.date.valueOf() - b.date.valueOf());
  return out;
}

/**
 * Reservations time series from reservations.reservations_month.
 * Returns [{ label: 'DD/MM', value, date }] sorted chronologically.
 */
export function toReservationsSeries(reservations) {
  if (!reservations || typeof reservations !== 'object') return [];
  const month = reservations.reservations_month;
  if (!month || typeof month !== 'object') return [];
  const out = [];
  for (const [key, entry] of Object.entries(month)) {
    const date = parseFriendlyDate(key);
    if (!date) continue;
    const value = safeNumber(entry && typeof entry === 'object' ? entry.amount : entry);
    out.push({ label: date.format('DD/MM'), value, date });
  }
  out.sort((a, b) => a.date.valueOf() - b.date.valueOf());
  return out;
}

/**
 * Issues time series from issues.issues_month.
 * Returns [{ label: 'DD/MM', reportados, resueltos, date }] sorted chronologically.
 */
export function toIssuesSeries(issues) {
  if (!issues || typeof issues !== 'object') return [];
  const month = issues.issues_month;
  if (!month || typeof month !== 'object') return [];
  const out = [];
  for (const [key, entry] of Object.entries(month)) {
    const date = parseFriendlyDate(key);
    if (!date) continue;
    const e = entry && typeof entry === 'object' ? entry : {};
    out.push({
      label: date.format('DD/MM'),
      reportados: safeNumber(e.total_reported),
      resueltos: safeNumber(e.total_resolved),
      date,
    });
  }
  out.sort((a, b) => a.date.valueOf() - b.date.valueOf());
  return out;
}

/**
 * Bucket a daily series [{label, value, date}] into weekly buckets.
 * Groups by date.startOf('week'), sums value, labels the bucket with the
 * week start formatted as DD/MM. Returns [{label, value}].
 */
export function bucketWeekly(series) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const buckets = new Map();
  for (const point of series) {
    if (!point || !point.date) continue;
    const d = dayjs.isDayjs(point.date) ? point.date : dayjs(point.date);
    if (!d.isValid()) continue;
    const weekStart = d.startOf('week');
    const key = weekStart.valueOf();
    const existing = buckets.get(key);
    const value = safeNumber(point.value);
    if (existing) {
      existing.value += value;
    } else {
      buckets.set(key, { label: weekStart.format('DD/MM'), value });
    }
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
}

/**
 * Bucket an issues daily series [{label, reportados, resueltos, date}] into
 * weekly buckets summing both fields. Returns [{label, reportados, resueltos}].
 */
export function bucketIssuesWeekly(series) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const buckets = new Map();
  for (const point of series) {
    if (!point || !point.date) continue;
    const d = dayjs.isDayjs(point.date) ? point.date : dayjs(point.date);
    if (!d.isValid()) continue;
    const weekStart = d.startOf('week');
    const key = weekStart.valueOf();
    const existing = buckets.get(key);
    const reportados = safeNumber(point.reportados);
    const resueltos = safeNumber(point.resueltos);
    if (existing) {
      existing.reportados += reportados;
      existing.resueltos += resueltos;
    } else {
      buckets.set(key, {
        label: weekStart.format('DD/MM'),
        reportados,
        resueltos,
      });
    }
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
}

/**
 * Visits hourly series for the 24h range.
 * Reads visits.visits_month (one entry for today) and extracts the
 * access_used_by_hour dict. Returns 24 points
 * [{ label: '00:00', value, hour: 0 }, ..., { label: '23:00', value, hour: 23 }].
 */
export function toVisitsHourlySeries(visits) {
  if (!visits || !visits.visits_month) return [];
  const result = [];
  for (let h = 0; h < 24; h++) {
    result.push({ label: `${String(h).padStart(2, '0')}:00`, value: 0, hour: h });
  }
  for (const [, dayData] of Object.entries(visits.visits_month)) {
    const byHour = dayData?.access_used_by_hour;
    if (!byHour || typeof byHour !== 'object') continue;
    for (let h = 0; h < 24; h++) {
      result[h].value += safeNumber(byHour[String(h)]);
    }
  }
  return result;
}

/**
 * Bucket a daily series [{label, value, date}] into monthly buckets.
 * Groups by date.startOf('month'), sums value, labels the bucket with the
 * month start formatted as MM/YYYY. Returns [{label, value}].
 */
export function bucketMonthly(series) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const buckets = new Map();
  for (const point of series) {
    if (!point || !point.date) continue;
    const d = dayjs.isDayjs(point.date) ? point.date : dayjs(point.date);
    if (!d.isValid()) continue;
    const monthStart = d.startOf('month');
    const key = monthStart.valueOf();
    const existing = buckets.get(key);
    const value = safeNumber(point.value);
    if (existing) {
      existing.value += value;
    } else {
      buckets.set(key, { label: monthStart.format('MM/YYYY'), value });
    }
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
}

/**
 * Bucket an issues daily series [{label, reportados, resueltos, date}] into
 * monthly buckets summing both fields. Returns [{label, reportados, resueltos}].
 */
export function bucketIssuesMonthly(series) {
  if (!Array.isArray(series) || series.length === 0) return [];
  const buckets = new Map();
  for (const point of series) {
    if (!point || !point.date) continue;
    const d = dayjs.isDayjs(point.date) ? point.date : dayjs(point.date);
    if (!d.isValid()) continue;
    const monthStart = d.startOf('month');
    const key = monthStart.valueOf();
    const existing = buckets.get(key);
    const reportados = safeNumber(point.reportados);
    const resueltos = safeNumber(point.resueltos);
    if (existing) {
      existing.reportados += reportados;
      existing.resueltos += resueltos;
    } else {
      buckets.set(key, {
        label: monthStart.format('MM/YYYY'),
        reportados,
        resueltos,
      });
    }
  }
  return Array.from(buckets.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([, v]) => v);
}

/**
 * Visits by daytime. Fixed order: Madrugada, Mañana, Tarde, Noche.
 */
export function toDaytimeBar(visits) {
  if (!visits || typeof visits !== 'object') return [];
  const src = visits.total_visits_by_daytime || {};
  const order = [
    { key: 'madrugada', label: 'Madrugada' },
    { key: 'mañana', label: 'Mañana' },
    { key: 'tarde', label: 'Tarde' },
    { key: 'noche', label: 'Noche' },
  ];
  return order.map(({ key, label }) => ({
    name: label,
    value: safeNumber(src[key]),
  }));
}

/**
 * Issues by category. Handles both {cat: count} and {cat: {total, resolved}}.
 * Sorted desc, top 8 + "Otros" (sum of the rest).
 */
export function toCategoryBar(issues) {
  if (!issues || typeof issues !== 'object') return [];
  const src = issues.issues_by_category || {};
  const entries = [];
  for (const [cat, val] of Object.entries(src)) {
    if (!cat) continue;
    let count;
    if (val && typeof val === 'object') {
      count = safeNumber(val.total);
    } else {
      count = safeNumber(val);
    }
    entries.push({ name: String(cat), value: count });
  }
  entries.sort((a, b) => b.value - a.value);
  const top = entries.slice(0, 8);
  const rest = entries.slice(8);
  if (rest.length > 0) {
    const otros = rest.reduce((acc, e) => acc + e.value, 0);
    top.push({ name: 'Otros', value: otros });
  }
  return top;
}

/**
 * Reservations by amenity. Combines total_reservations_by_amenity and
 * estimated_charges_by_amenity. Sorted desc, top 10.
 * Returns [{name, value, cargos}].
 */
export function toAmenityBar(reservations) {
  if (!reservations || typeof reservations !== 'object') return [];
  const counts = reservations.total_reservations_by_amenity || {};
  const charges = reservations.estimated_charges_by_amenity || {};
  const entries = [];
  for (const [amenity, val] of Object.entries(counts)) {
    if (!amenity) continue;
    entries.push({
      name: String(amenity),
      value: safeNumber(val),
      cargos: safeNumber(charges[amenity]),
    });
  }
  entries.sort((a, b) => b.value - a.value);
  return entries.slice(0, 10);
}

/**
 * Recharts <Tooltip> props that respect the active theme.
 */
export function chartTooltipProps(theme) {
  if (!theme || !theme.palette) {
    return {
      contentStyle: { backgroundColor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 8, color: '#000' },
      labelStyle: { color: '#000' },
      itemStyle: { color: 'rgba(0,0,0,0.6)' },
    };
  }
  const p = theme.palette;
  return {
    contentStyle: {
      backgroundColor: p.background.paper,
      border: '1px solid ' + p.divider,
      borderRadius: 8,
      color: p.text.primary,
    },
    labelStyle: { color: p.text.primary },
    itemStyle: { color: p.text.secondary },
  };
}

/**
 * Recharts XAxis/YAxis props that respect the active theme.
 */
export function chartAxisProps(theme) {
  if (!theme || !theme.palette) {
    return { tick: { fill: 'rgba(0,0,0,0.6)', fontSize: 12 }, stroke: '#e0e0e0' };
  }
  return {
    tick: { fill: theme.palette.text.secondary, fontSize: 12 },
    stroke: theme.palette.divider,
  };
}
